import { Stack } from '@mantine/core';
import { IconDownload } from '@tabler/icons-react';
import { BenefitType, PolicyType, SelfEnrollType } from '@zorro/clients';
import { Alert } from '@zorro/design-system/v2/Alert';
import { Autocomplete } from '@zorro/design-system/v2/Autocomplete';
import { Button } from '@zorro/design-system/v2/Button';
import { Checkbox } from '@zorro/design-system/v2/Checkbox';
import { MultiSelect } from '@zorro/design-system/v2/MultiSelect';
import { Select } from '@zorro/design-system/v2/Select';
import { TextInput } from '@zorro/design-system/v2/TextInput';
import {
  CurrencyInput,
  FormErrorMessage,
  Grid,
  Group,
  Icon,
  Text,
} from '@zorro/web/design-system';
import {
  MonolithMethodNames,
  MonolithMethods,
  UseMonolithQueryProps,
  useMonolithQuery,
} from '@zorro/web/ui-utils';
import { KeyboardEvent, useEffect, useMemo } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';

import { useAnalytics } from '../Analytics';
import { CarrierInput } from '../CarrierInput';
import {
  PlanElectionFormFields,
  PlanElectionItem,
  PlanElectionSource,
  formatwhoIsEnrolledInMultiplePlans,
  mapBenefitTypeToPlanElectionFlow,
  policyTypeOptions,
} from './PlanElectionFormUtils';

type Props<T extends MonolithMethodNames> = {
  method: T;
  getParams: (whoIsEnrolled: string[]) => UseMonolithQueryProps<T>['params'];
  mapDataToPlanElectionItem: (
    data?: Awaited<ReturnType<MonolithMethods[T]>>
  ) => PlanElectionItem[] | undefined;
  planElectionForm: UseFormReturn<PlanElectionFormFields>;
  insuredIdToNameMap: Map<string, string>;
  onDeleteDependent?: (index: number) => void;
  hasMoreThanOnePlan: boolean;
  whoIsEnrolledOptions: string[];
  whoIsEnrolledInMultiplePlans: string[];
  onDownloadPlans: (data?: Awaited<ReturnType<MonolithMethods[T]>>) => void;
  isCombinedPlanStructure?: boolean;
  benefitType: BenefitType;
  source: PlanElectionSource;
  index: number;
};

export const PlanElectionFormInputs = <T extends MonolithMethodNames>({
  mapDataToPlanElectionItem,
  whoIsEnrolledInMultiplePlans,
  isCombinedPlanStructure = false,
  onDeleteDependent,
  whoIsEnrolledOptions,
  insuredIdToNameMap,
  hasMoreThanOnePlan,
  planElectionForm,
  onDownloadPlans,
  benefitType,
  getParams,
  method,
  source,
  index,
}: Props<T>) => {
  const { control, watch, formState, setValue } = planElectionForm;
  const { sendEvent } = useAnalytics();
  const { errors } = formState;

  const plan = watch(`plans.${index}`);
  const { whoIsEnrolled, planId } = plan;

  const isMedicareCoverageOn = watch(`plans.${index}.isMedicareCoverage`);

  const { data, isLoading: isLoadingPlans } = useMonolithQuery({
    method,
    params: getParams(whoIsEnrolled),
  });

  const plans = useMemo(() => mapDataToPlanElectionItem(data), [data]);

  const [allPlanIdsRecord, carrierNames] = useMemo(
    () => [
      plans?.reduce<Record<string, string>>(
        (record, { id, name, carrierName }) => {
          record[id] = `${carrierName}, ${name}`;

          return record;
        },
        {}
      ) || {},
      [...new Set<string>(plans?.map(({ carrierName }) => carrierName))],
    ],
    [plans]
  );

  useEffect(() => {
    if (plans) {
      const foundPlan = plans.find(({ id }) => id === planId);
      if (!foundPlan) {
        return;
      }

      const {
        carrierName = '',
        name,
        premium,
        deductible,
        planMarket,
        hsaEligible,
        maxOutOfPocket,
        benefitsSummaryUrl,
        policyType,
        extendedPlanDetails,
        hasVisionRider,
      } = foundPlan;

      setValue(`plans.${index}.carrier`, carrierName);
      setValue(`plans.${index}.planName`, name || '');
      setValue(`plans.${index}.premium`, premium || 0);
      setValue(`plans.${index}.isHsaEligible`, hsaEligible ?? false);
      setValue(`plans.${index}.maxOutOfPocket`, maxOutOfPocket);
      setValue(`plans.${index}.benefitsSummaryUrl`, benefitsSummaryUrl);
      setValue(`plans.${index}.deductible`, deductible);
      setValue(`plans.${index}.planMarket`, planMarket);
      setValue(`plans.${index}.planType`, policyType || PolicyType.IFP);
      setValue(`plans.${index}.combinedPlanInfo`, extendedPlanDetails ?? '');
      setValue(`plans.${index}.hasVisionRider`, hasVisionRider ?? false);
    }
  }, [plans, planId, index, setValue]);

  const whoIsEnrolledOptionsMap = useMemo(() => {
    return whoIsEnrolledOptions.map((id) => ({
      label: insuredIdToNameMap.get(id) || '',
      value: id,
    }));
  }, [insuredIdToNameMap, whoIsEnrolledOptions]);

  const handleWhoIsEnrolledChange = async (updatedWhoIsEnrolled: string[]) => {
    await sendEvent('plan_election_covered_members_changed', {
      flow: mapBenefitTypeToPlanElectionFlow(benefitType),
      covered_count: updatedWhoIsEnrolled.length,
      added_count: updatedWhoIsEnrolled.filter(
        (insuredId) => !whoIsEnrolled.includes(insuredId)
      ).length,
      removed_count: whoIsEnrolled.filter(
        (insuredId) => !updatedWhoIsEnrolled.includes(insuredId)
      ).length,
      plan_index: index,
      source,
    });
  };

  // The selected plan's self-enroll overrides the benefit's
  const applySelfEnrollFromPlan = (selectedPlan?: PlanElectionItem) => {
    setValue(
      `plans.${index}.isSelfEnroll`,
      selectedPlan?.selfEnroll === SelfEnrollType.SELF_ENROLL
    );
  };

  const handlePlanSelect = async (selectedPlanId: string) => {
    const selectedPlan = plans?.find(({ id }) => id === selectedPlanId);

    applySelfEnrollFromPlan(selectedPlan);

    await sendEvent('plan_election_plan_selected', {
      flow: mapBenefitTypeToPlanElectionFlow(benefitType),
      carrier_name: selectedPlan?.carrierName,
      plan_id: selectedPlanId,
      plan_index: index,
      source,
    });
  };

  const handleFieldChange = async (field: string, value: string | boolean) => {
    await sendEvent('plan_election_field_changed', {
      flow: mapBenefitTypeToPlanElectionFlow(benefitType),
      plan_index: index,
      field,
      value,
      source,
    });
  };

  const handleDownloadPlansClick = async () => {
    onDownloadPlans(data);

    await sendEvent('plan_election_plans_downloaded', {
      flow: mapBenefitTypeToPlanElectionFlow(benefitType),
      plan_count: plans?.length,
      plan_index: index,
      source,
    });
  };

  // A typed or pasted id skips onOptionSubmit, so resolve self-enroll on blur
  const handleBlurPlanSelection = (candidatePlanId?: string) => {
    applySelfEnrollFromPlan(plans?.find(({ id }) => id === candidatePlanId));
  };

  return (
    <Grid>
      <Grid.Col span={12}>
        <Controller
          name={`plans.${index}.whoIsEnrolled`}
          control={control}
          render={({ field: { value, onChange, ...rest } }) => {
            return (
              <MultiSelect
                {...rest}
                comboboxProps={{ withinPortal: false }}
                onChange={(updatedWhoIsEnrolled) => {
                  onChange(updatedWhoIsEnrolled);
                  void handleWhoIsEnrolledChange(updatedWhoIsEnrolled);
                }}
                value={value}
                size="md"
                data={whoIsEnrolledOptionsMap}
                label="Who is covered?*"
                placeholder="Who is covered"
              />
            );
          }}
        />

        <FormErrorMessage
          fieldName={`plans.${index}.whoIsEnrolled`}
          errors={errors}
        />
      </Grid.Col>
      {whoIsEnrolledInMultiplePlans.length > 0 && (
        <Grid.Col>
          <Alert variant="general-note" title="General note" withCloseButton>
            {formatwhoIsEnrolledInMultiplePlans(
              whoIsEnrolledInMultiplePlans,
              insuredIdToNameMap,
              benefitType
            )}
          </Alert>
        </Grid.Col>
      )}
      <Grid.Col>
        {isCombinedPlanStructure ? (
          <>
            <Controller
              control={control}
              name={`plans.${index}.planType`}
              render={({ field: { onChange, ...rest } }) => (
                <Select
                  {...rest}
                  size="md"
                  placeholder="Plan type"
                  data={policyTypeOptions()}
                  onChange={(planType) => {
                    onChange(planType);
                    void handleFieldChange('plan_type', planType || '');
                  }}
                  label="Plan type"
                  required
                />
              )}
            />
            <FormErrorMessage
              fieldName={`plans.${index}.planType`}
              errors={errors}
            />
          </>
        ) : (
          <Controller
            control={control}
            name={`plans.${index}.isMedicareCoverage`}
            render={({ field: { value, onChange, ...rest } }) => (
              <Checkbox
                {...rest}
                checked={value}
                onChange={(event) => {
                  onChange(event);
                  void handleFieldChange(
                    'is_medicare_coverage',
                    event.currentTarget.checked
                  );
                }}
                label="Medicare coverage"
              />
            )}
          />
        )}
      </Grid.Col>

      {!isMedicareCoverageOn && (
        <Grid.Col>
          <Controller
            name={`plans.${index}.planId`}
            control={control}
            render={({ field: { onBlur, value, ...rest } }) => (
              <Autocomplete
                {...rest}
                value={value}
                size="md"
                placeholder="Type plan ID"
                comboboxProps={{ withinPortal: false }}
                onOptionSubmit={(selectedPlanId) =>
                  void handlePlanSelect(selectedPlanId)
                }
                onBlur={() => {
                  onBlur();
                  handleBlurPlanSelection(value);
                }}
                disabled={isLoadingPlans}
                // eslint-disable-next-line id-length
                labelProps={{ w: '100%' }}
                label={
                  <Group justify="space-between">
                    <Text size="sm">Plan ID</Text>
                    {!isCombinedPlanStructure && (
                      <Button
                        leftSection={<Icon icon={IconDownload} />}
                        onClick={handleDownloadPlansClick}
                        disabled={!plans || isLoadingPlans}
                        variant="tertiary"
                        size="sm"
                      >
                        Download all plans
                      </Button>
                    )}
                  </Group>
                }
                data={Object.keys(allPlanIdsRecord)}
                renderOption={({ option: { value } }) => (
                  <Stack gap="0.125rem">
                    <Text>{value}</Text>
                    <Text c="zorroGray.4">{allPlanIdsRecord[value]}</Text>
                  </Stack>
                )}
              />
            )}
          />

          <FormErrorMessage
            fieldName={`plans.${index}.planId`}
            errors={errors}
          />
        </Grid.Col>
      )}

      <Grid.Col>
        {isMedicareCoverageOn ? (
          <>
            <Controller
              name={`plans.${index}.medicareProvider`}
              control={control}
              render={({ field: { ...rest } }) => (
                <TextInput
                  {...rest}
                  label="Medicare provider"
                  placeholder="Medicare provider"
                  size="md"
                  required
                />
              )}
            />

            <FormErrorMessage
              fieldName={`plans.${index}.medicareProvider`}
              errors={errors}
            />
          </>
        ) : (
          <CarrierInput
            carriers={carrierNames.length > 0 ? carrierNames : undefined}
            name={`plans.${index}.carrier`}
            control={control}
            size="md"
            shouldShowError
            isEditable={isMedicareCoverageOn}
            isRequired
            shouldShowNotListedCheckbox={false}
            benefitType={benefitType}
          />
        )}
      </Grid.Col>

      <Grid.Col span={isCombinedPlanStructure ? 12 : 6}>
        <Controller
          name={`plans.${index}.planName`}
          control={control}
          render={({ field: { ...rest } }) => (
            <TextInput
              {...rest}
              label="Plan name"
              placeholder="Plan name"
              size="md"
              required
            />
          )}
        />

        <FormErrorMessage
          fieldName={`plans.${index}.planName`}
          errors={errors}
        />
      </Grid.Col>

      <Grid.Col span={isCombinedPlanStructure ? 12 : 6}>
        <Controller
          name={`plans.${index}.premium`}
          control={control}
          render={({ field: { ...rest } }) => (
            <CurrencyInput
              {...rest}
              label="Premium"
              placeholder="$"
              allowNegative={false}
              decimalScale={2}
              size="md"
              required
            />
          )}
        />

        <FormErrorMessage
          fieldName={`plans.${index}.premium`}
          errors={errors}
        />
      </Grid.Col>

      {isCombinedPlanStructure && (
        <Grid.Col>
          <>
            <Controller
              control={control}
              name={`plans.${index}.combinedPlanInfo`}
              render={({ field }) => (
                <TextInput
                  {...field}
                  size="md"
                  placeholder="Combined plan info"
                  label="Combined plan info"
                  required
                />
              )}
            />

            <FormErrorMessage
              fieldName={`plans.${index}.combinedPlanInfo`}
              errors={errors}
            />
          </>
        </Grid.Col>
      )}

      <Grid.Col>
        <Controller
          control={control}
          name={`plans.${index}.isSelfEnroll`}
          render={({ field: { value, onChange, ...rest } }) => (
            <Checkbox
              {...rest}
              checked={value}
              onChange={(event) => {
                onChange(event);
                void handleFieldChange(
                  'is_self_enroll',
                  event.currentTarget.checked
                );
              }}
              label="Self-enroll required"
            />
          )}
        />
      </Grid.Col>

      {hasMoreThanOnePlan && onDeleteDependent && (
        <Grid.Col style={{ textAlign: 'right' }}>
          <Button
            variant="tertiary"
            onClick={() => onDeleteDependent(index)}
            onKeyDown={(event: KeyboardEvent<HTMLButtonElement>) => {
              if (event.code === 'Space' || event.code === 'Enter') {
                onDeleteDependent(index);
              }
            }}
          >
            Delete
          </Button>
        </Grid.Col>
      )}
    </Grid>
  );
};
